import { useCallback, useEffect, useMemo, useState } from 'react'

import { pocketbaseErrorMessage } from '../../lib/pocketbaseErrorMessage'
import {
  COL_MACHINE_EVENTS,
  COL_MACHINES,
  COL_SCHEDULE_SLOTS,
  COL_SHIFTS,
} from '../../config/collections'
import { PERMISSION_KEYS } from '../../config/permissions'
import { groupMachinesByType, machineTypeAccent, machineTypeLabel } from '../../lib/machines'
import { fetchCalendarMap } from '../../lib/loadProductionCalendar'
import { calendarDayDetailLabel, resolvePlantIsWorking } from '../../lib/productionCalendar'
import { brigadeLabel, resolveWorkingBrigade } from '../../lib/shiftRotation'
import { pb } from '../../pocketbase'
import { useAuth } from '../../context/AuthContext'
import {
  formatDateTimeRu,
  ganttBarStyle,
  hourLabels,
  MACHINE_EVENT_REASON_LABELS,
  SCHEDULE_SLOT_STATUS_LABELS,
} from '../../lib/scheduleCalculations'
import type { MachineEventRecord, MachineRecord, ScheduleSlotRecord, ShiftRecord } from '../../types/records'
import { Alert, Button } from '../../components/ui'
import { ScheduleSlotFormModal } from './ScheduleSlotFormModal'
import { GanttDraggableSlot } from './GanttDraggableSlot'
import { btn, cn, inputClass, loadingClass, toolbarClass } from '../../lib/ui'

const GANTT_HOURS = 24
const ROW_HEIGHT = 44

function dateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const SLOT_STATUS_COLORS: Record<string, string> = {
  planned: '#6366f1',
  in_progress: '#059669',
  completed: '#64748b',
  cancelled: '#94a3b8',
}

export function ScheduleGanttPage() {
  const { user, can } = useAuth()
  const canAllShifts = can(PERMISSION_KEYS.PRODUCTION_SCHEDULE_ALL_SHIFTS)
  const canWrite = can(PERMISSION_KEYS.PRODUCTION_SCHEDULE_WRITE)

  const [day, setDay] = useState(() => dateInputValue(new Date()))
  const [shiftId, setShiftId] = useState(() =>
    canAllShifts ? '' : (typeof user?.assignedShift === 'string' ? user.assignedShift : ''),
  )
  const [machines, setMachines] = useState<MachineRecord[]>([])
  const [shifts, setShifts] = useState<ShiftRecord[]>([])
  const [slots, setSlots] = useState<ScheduleSlotRecord[]>([])
  const [events, setEvents] = useState<MachineEventRecord[]>([])
  const [calendarMap, setCalendarMap] = useState(
    () => new Map<string, { isWorking: boolean; workingBrigade?: string }>(),
  )
  const [shiftTouched, setShiftTouched] = useState(false)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')

  const [modalSlotId, setModalSlotId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMachineId, setModalMachineId] = useState<string | undefined>()
  const [modalStart, setModalStart] = useState<string | undefined>()
  const [dragMsg, setDragMsg] = useState('')

  const rangeStart = useMemo(() => {
    const d = new Date(`${day}T00:00:00`)
    return d
  }, [day])

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const dayStart = `${day} 00:00:00`
      const dayEnd = `${day} 23:59:59`
      let slotFilter = `plannedStart <= "${dayEnd}" && plannedEnd >= "${dayStart}" && status != "cancelled"`
      if (shiftId) slotFilter += ` && shift="${shiftId}"`

      const [mList, sList, slotList, evList, calMap] = await Promise.all([
        pb.collection(COL_MACHINES).getFullList<MachineRecord>({ sort: 'type,sort,number' }),
        pb.collection(COL_SHIFTS).getFullList<ShiftRecord>({ sort: 'sort' }),
        pb.collection(COL_SCHEDULE_SLOTS).getFullList<ScheduleSlotRecord>({
          filter: slotFilter,
          expand: 'requestLine.product,machine,shift',
          sort: 'plannedStart',
        }),
        pb.collection(COL_MACHINE_EVENTS).getFullList<MachineEventRecord>({
          filter: `startAt <= "${dayEnd}" && (endAt = "" || endAt >= "${dayStart}")`,
          sort: 'startAt',
        }),
        fetchCalendarMap(day, day),
      ])
      setMachines(mList)
      setShifts(sList.filter((s) => s.active))
      setSlots(slotList)
      setEvents(evList)
      setCalendarMap(calMap)
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки графика'))
      setMachines([])
      setSlots([])
      setEvents([])
    } finally {
      setLoading(false)
    }
  }, [day, shiftId])

  useEffect(() => {
    setShiftTouched(false)
  }, [day])

  useEffect(() => {
    if (shiftTouched) return
    const brigade = resolveWorkingBrigade(day, calendarMap)
    if (brigade) setShiftId(brigade)
  }, [day, calendarMap, shiftTouched])

  useEffect(() => {
    void load()
  }, [load])

  const machineGroups = useMemo(() => groupMachinesByType(machines), [machines])
  const hours = useMemo(() => hourLabels(0, GANTT_HOURS), [])
  const shiftsById = useMemo(() => new Map(shifts.map((s) => [s.id, s])), [shifts])
  const plantWorking = useMemo(() => resolvePlantIsWorking(day, calendarMap), [day, calendarMap])
  const dayCalendarLabel = useMemo(
    () => calendarDayDetailLabel(plantWorking, day),
    [plantWorking, day],
  )
  const brigadeDayLabel = useMemo(
    () => brigadeLabel(day, calendarMap, shiftsById),
    [day, calendarMap, shiftsById],
  )

  function openNew(machineId?: string) {
    setModalSlotId(null)
    setModalMachineId(machineId)
    setModalStart(`${day}T08:00:00`)
    setModalOpen(true)
  }

  function openEdit(slotId: string) {
    setModalSlotId(slotId)
    setModalMachineId(undefined)
    setModalStart(undefined)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setModalSlotId(null)
  }

  if (loading && machines.length === 0) {
    return <div className={loadingClass}>Загрузка графика…</div>
  }

  return (
    <div>
      <p className="mb-4 text-sm text-su-text">
        Почасовой Gantt по станкам. Серые полоски — простои. Нажмите на слот для редактирования
        {canWrite ? ' или перетащите для переноса' : ''}. Календарь цеха: {dayCalendarLabel}. Бригада 2/2:{' '}
        {brigadeDayLabel}.
      </p>
      {dragMsg && (
        <p className="mb-3 rounded-su border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
          {dragMsg}
        </p>
      )}
      {!plantWorking && (
        <Alert kind="warn">День отмечен как простой/праздник — планирование слотов не рекомендуется.</Alert>
      )}
      {err && <Alert kind="err">{err}</Alert>}

      <div className={toolbarClass}>
        <label className="flex items-center gap-2 text-sm">
          <span className="font-medium text-su-text">Дата</span>
          <input type="date" className={inputClass} value={day} onChange={(e) => setDay(e.target.value)} />
        </label>
        <label className="flex items-center gap-2 text-sm">
          <span className="font-medium text-su-text">Смена</span>
          <select
            className={cn(inputClass, 'min-w-40')}
            value={shiftId}
            onChange={(e) => {
              setShiftTouched(true)
              setShiftId(e.target.value)
            }}
            disabled={!canAllShifts && !!user?.assignedShift}
          >
            {canAllShifts && <option value="">Все смены</option>}
            {shifts.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <Button type="button" onClick={() => void load()} disabled={loading}>
          Обновить
        </Button>
        {canWrite && (
          <Button type="button" variant="primary" onClick={() => openNew()}>
            Новый слот
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-su-lg border border-su-border bg-su-surface shadow-su-md">
        <div className="min-w-[960px]">
          <div className="grid border-b border-su-border bg-su-surface-muted" style={{ gridTemplateColumns: '180px 1fr' }}>
            <div className="border-r border-su-border px-3 py-2 text-xs font-semibold text-slate-500">Станок</div>
            <div className="relative flex">
              {hours.map((h, i) => (
                <div
                  key={h}
                  className="flex-1 border-r border-su-border/60 px-0.5 py-2 text-center text-[0.65rem] text-slate-500 last:border-r-0"
                  style={{ minWidth: 32 }}
                >
                  {i % 2 === 0 ? h : ''}
                </div>
              ))}
            </div>
          </div>

          {machineGroups.map((group) => (
            <div key={group.type}>
              <div
                className="border-b border-su-border px-3 py-1.5 text-xs font-semibold"
                style={{ color: group.accent, backgroundColor: `${group.accent}12` }}
              >
                {group.label}
              </div>
              {group.items.map((machine) => {
                const machineSlots = slots.filter((s) => s.machine === machine.id)
                const machineEvents = events.filter((e) => e.machine === machine.id)
                return (
                  <div
                    key={machine.id}
                    className="grid border-b border-su-border last:border-b-0"
                    style={{ gridTemplateColumns: '180px 1fr', minHeight: ROW_HEIGHT }}
                    data-gantt-machine-id={machine.id}
                  >
                    <div className="flex items-center border-r border-su-border px-3 py-2 text-sm">
                      <span
                        className="mr-2 inline-block h-2 w-2 shrink-0 rounded-full"
                        style={{ backgroundColor: machineTypeAccent(machine.type) }}
                      />
                      <span className="font-medium">{machineTypeLabel(machine.type)} №{machine.number}</span>
                      {canWrite && (
                        <button
                          type="button"
                          className={cn(btn('ghost', 'sm'), 'ml-auto')}
                          title="Добавить слот"
                          onClick={() => openNew(machine.id)}
                        >
                          +
                        </button>
                      )}
                    </div>
                    <div
                      className="relative"
                      style={{
                        backgroundImage:
                          'repeating-linear-gradient(90deg, transparent, transparent calc(100% / 24 - 1px), rgba(148,163,184,0.15) calc(100% / 24 - 1px), rgba(148,163,184,0.15) calc(100% / 24))',
                      }}
                    >
                      {machineEvents.map((ev) => {
                        const evEnd = ev.endAt ?? new Date(`${day}T23:59:59`).toISOString()
                        const style = ganttBarStyle(ev.startAt, evEnd, rangeStart, GANTT_HOURS)
                        if (!style) return null
                        return (
                          <div
                            key={ev.id}
                            className="absolute top-1 z-10 h-[calc(100%-8px)] rounded border border-slate-400/50 bg-slate-400/35"
                            style={{ ...style, minWidth: 4 }}
                            title={`${MACHINE_EVENT_REASON_LABELS[ev.reason] ?? ev.reason}: ${formatDateTimeRu(ev.startAt)}`}
                          />
                        )
                      })}
                      {machineSlots.map((slot) => {
                        const color = SLOT_STATUS_COLORS[slot.status] ?? '#6366f1'
                        const productName = slot.expand?.requestLine?.expand?.product?.name ?? 'Слот'
                        return (
                          <GanttDraggableSlot
                            key={slot.id}
                            slot={slot}
                            machine={machine}
                            machines={machines}
                            rangeStart={rangeStart}
                            hoursInDay={GANTT_HOURS}
                            color={color}
                            productName={productName}
                            canDrag={canWrite}
                            userId={user?.id}
                            onEdit={() => openEdit(slot.id)}
                            onMoved={(msg) => {
                              setDragMsg(msg)
                              void load()
                            }}
                            onError={setErr}
                          />
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          ))}

          {machines.length === 0 && (
            <p className="p-6 text-center text-sm text-su-text">
              <em>Нет станков — добавьте в админке или примените миграции.</em>
            </p>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-3 text-xs text-su-text">
        {Object.entries(SCHEDULE_SLOT_STATUS_LABELS).map(([key, label]) => (
          <span key={key} className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-4 rounded" style={{ backgroundColor: SLOT_STATUS_COLORS[key] }} />
            {label}
          </span>
        ))}
        <span className="flex items-center gap-1">
          <span className="inline-block h-2.5 w-4 rounded bg-slate-400/50" />
          Простой
        </span>
      </div>

      {modalOpen && (
        <ScheduleSlotFormModal
          slotId={modalSlotId}
          initialMachineId={modalMachineId}
          initialStart={modalStart}
          onClose={closeModal}
          onSaved={() => void load()}
        />
      )}
    </div>
  )
}
